'use client';

import React, { useEffect, useRef } from 'react';
import { Shield, Clock } from 'lucide-react';

const accreditations = [
  {
    id: 'acc-001',
    name: 'NQF Alignment',
    full: 'National Qualifications Framework',
    desc: 'Programmes are being designed for future NQF alignment. Formal SAQA registration is in progress.',
  },
  {
    id: 'acc-002',
    name: 'PPRA / EAAB',
    full: 'Property Practitioners Regulatory Authority',
    desc: 'Programmes are structured to support PPRA compliance pathways. Formal recognition is in progress.',
  },
  {
    id: 'acc-003',
    name: 'CPD Pathways',
    full: 'Continuing Professional Development',
    desc: 'Programmes are designed to support CPD requirements. Formal CPD recognition is being pursued.',
  },
  {
    id: 'acc-004',
    name: 'RICS-Aligned Curriculum',
    full: 'Royal Institution of Chartered Surveyors',
    desc: 'Valuation and surveying content is informed by RICS frameworks. Formal RICS recognition is in progress.',
  },
  {
    id: 'acc-005',
    name: 'Industry-Informed',
    full: 'Built for African Real Estate Professionals',
    desc: 'Programme content is developed in consultation with active industry practitioners across the African property sector.',
  },
  {
    id: 'acc-006',
    name: 'Skills Development',
    full: 'SETA & Learnership Pathways',
    desc: 'Programmes are being structured for potential SETA alignment and skills funding eligibility. Applications in progress.',
  },
];

export default function AccreditationSection() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('visible');
        });
      },
      { threshold: 0.1 }
    );
    const els = ref?.current?.querySelectorAll('.section-reveal, .stagger-child');
    els?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section className="section-pad bg-card border-y border-border" id="accreditation" ref={ref}>
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 xl:px-16">
        <div className="text-center mb-12 section-reveal">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="divider-gold" />
            <span className="label-caps">Accreditation & Alignment</span>
            <div className="divider-gold" />
          </div>
          <h2 className="font-serif text-section-title text-foreground">
            Industry-Informed Programmes,
            <br />
            <span className="italic">Built for Africa</span>
          </h2>
          <p className="font-sans text-sm text-muted-foreground mt-4 max-w-xl mx-auto leading-relaxed">
            REUSA programmes are designed with future regulatory alignment in mind. Formal accreditation and registration processes are currently in progress.
          </p>
        </div>

        {/* Accreditation cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-14">
          {accreditations?.map((acc, i) => (
            <div
              key={acc?.id}
              className="flex gap-4 p-5 rounded-xl border border-border bg-background hover:border-accent/40 hover:shadow-sm transition-all duration-300 stagger-child"
              style={{ transitionDelay: `${i * 80}ms` }}
            >
              <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center flex-shrink-0">
                <Shield size={18} className="text-primary" />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-sans font-bold text-sm text-primary">{acc?.name}</span>
                  <Clock size={13} className="text-muted-foreground" />
                </div>
                <p className="font-sans text-xs font-medium text-muted-foreground mb-1">{acc?.full}</p>
                <p className="font-sans text-xs text-muted-foreground/70 leading-relaxed">{acc?.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Industry note */}
        <div className="border-t border-border pt-10 section-reveal">
          <p className="text-center font-sans text-sm text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            REUSA is an independent professional and executive education provider. Our programmes are designed for African real estate professionals and are being developed for future NQF alignment where applicable. We do not currently claim formal accreditation by SAQA, SETA, EAAB, SACAP, RICS, or SAPOA unless explicitly confirmed in writing.
          </p>
        </div>
      </div>
    </section>
  );
}